/**
 * capabilities 复用预设。
 *
 * 每个导出都是一个完整的 capability 对象，直接放进 manifest 的 `capabilities` 数组即可：
 *
 * ```ts
 * capabilities: [retailMouseConfig, parametersLiftoffLow, dpi60kStep1, mouseMacro],
 * ```
 *
 * 约定：
 * - 取值相同的能力一律引用这里的预设，不要在 manifest 里复制字面量；改一处即可覆盖全部机型。
 * - 只有一款机型独有的取值，仍然写成 manifest 内联字面量，让「这台确实特殊」一眼可见。
 * - 这些对象被多个 manifest 共享同一份实例，属于只读配置，任何地方都不得修改其内容。
 */

import type {
  BaseChannelCapability,
  BaseChildDeviceCapability,
  BaseScreenCapability,
  ConfigCapability,
  DpiCapability,
  InputReportCapability,
  ItineraryCapability,
  KeyFeaturesCapability,
  KeyScanRate,
  KeyboardPerfCapability,
  MacroCapability,
  MousePerfCapability,
  MouseRTCapability,
  ParametersCapability,
  ReportRate,
  SensorModeOption,
} from '@rapoo/device-sdk'
import { cafe54ConfigSlots, esk900ConfigSlots } from './configGenerate'

/* -------------------------------------------------------------------------- */
/* config：配置槽位与配置名长度                                                 */
/* -------------------------------------------------------------------------- */

/** 零售鼠标：8 个配置，配置名上限 16 字节。 */
export const retailMouseConfig: ConfigCapability = {
  type: 'config',
  config: {
    maxNameLen: 0x10,
    maxConfig: 8,
  },
}

/** 零售键盘：4 个配置，配置名上限 16 字节。 */
export const retailKeyboardConfig: ConfigCapability = {
  type: 'config',
  config: {
    maxNameLen: 0x10,
    maxConfig: 4,
  },
}

/** 网吧 54 系列鼠标：4 个出厂配置 + 4 个用户配置，按角色区分可见性。 */
export const cafe54MouseConfig: ConfigCapability = {
  type: 'config',
  config: {
    maxNameLen: 0x10,
    maxConfig: cafe54ConfigSlots.length,
    configSlots: cafe54ConfigSlots,
  },
}

/** 网吧 ESK900 系列键盘：首个槽位为出厂配置，且支持管理员出厂模式。 */
export const esk900KeyboardConfig: ConfigCapability = {
  type: 'config',
  config: {
    maxNameLen: 0x10,
    maxConfig: esk900ConfigSlots.length,
    configSlots: esk900ConfigSlots,
    adminFactory: true,
  },
}

/** GCOME_AP 键盘：3 个配置，配置名 3-30 字节。 */
export const gcomeApKeyboardConfig: ConfigCapability = {
  type: 'config',
  config: {
    minNameLen: 3,
    maxNameLen: 0x1e,
    maxConfig: 3,
  },
}

/* -------------------------------------------------------------------------- */
/* parameters：静默高度                                                        */
/* -------------------------------------------------------------------------- */

type LiftoffHeight = ParametersCapability['config']['liftoffHeight']

/** 静默高度低区间 0.7-1.7mm（PAW3955U 等新传感器）。 */
export const liftoffHeightLow: LiftoffHeight = [
  0.7, 0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7,
]

/** 静默高度高区间 1.0-2.0mm（PAW3398 / PAW3950U 等老传感器）。 */
export const liftoffHeightHigh: LiftoffHeight = [
  1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2.0,
]

export const parametersLiftoffLow: ParametersCapability = {
  type: 'parameters',
  config: {
    liftoffHeight: liftoffHeightLow,
  },
}

export const parametersLiftoffHigh: ParametersCapability = {
  type: 'parameters',
  config: {
    liftoffHeight: liftoffHeightHigh,
  },
}

/* -------------------------------------------------------------------------- */
/* dpi                                                                        */
/* -------------------------------------------------------------------------- */

/** 1-60000 连续可调（V2 代零售鼠标）。 */
export const dpi60kStep1: DpiCapability = {
  type: 'dpi',
  config: {
    min: 1,
    max: 60000,
    step: 1,
  },
}

/** 10-45000 分段步进：10050 以下 10 一档，以上 50 一档。 */
export const dpi45kStepped: DpiCapability = {
  type: 'dpi',
  config: {
    min: 10,
    max: 45000,
    step: [
      { from: 10, to: 10050, step: 10 },
      { from: 10050, to: 45000, step: 50 },
    ],
  },
}

/** 50-26000，固定 50 一档。 */
export const dpi26kStep50: DpiCapability = {
  type: 'dpi',
  config: {
    min: 50,
    max: 26000,
    step: 50,
  },
}

/* -------------------------------------------------------------------------- */
/* macro：单条宏编码上限（含 4 字节头部）                                        */
/* -------------------------------------------------------------------------- */

export const mouseMacro: MacroCapability = {
  type: 'macro',
  config: {
    maxLength: 4096,
  },
}

export const keyboardMacro: MacroCapability = {
  type: 'macro',
  config: {
    maxLength: 512,
  },
}

/** ESM 网吧鼠标单条宏上限，协议槽位 1024 字节。写满时的短尾包由前端按动作截掉。 */
export const cafeMouseMacro: MacroCapability = {
  type: 'macro',
  config: {
    maxLength: 1024,
  },
}

/* -------------------------------------------------------------------------- */
/* input-report：主动上报解析分支                                               */
/* -------------------------------------------------------------------------- */

export const inputReportCybercafe: InputReportCapability = {
  type: 'input-report',
  config: {
    type: 'cybercafe',
  },
}

export const inputReportV700: InputReportCapability = {
  type: 'input-report',
  config: {
    type: 'V700',
  },
}

export const inputReportRetail: InputReportCapability = {
  type: 'input-report',
  config: {
    type: 'retail',
  },
}

/* -------------------------------------------------------------------------- */
/* itinerary：键盘行程                                                         */
/* -------------------------------------------------------------------------- */

export const itineraryDisabled: ItineraryCapability = {
  type: 'itinerary',
  config: {
    hasTravel: false,
  },
}

export const itineraryEnabled: ItineraryCapability = {
  type: 'itinerary',
  config: {
    hasTravel: true,
  },
}

/** GCOME_AP 行程：支持 0.01 / 0.005 / 0.001mm 三档 RT 精度。 */
export const itineraryGcomeAp: ItineraryCapability = {
  type: 'itinerary',
  config: {
    hasTravel: true,
    rtPrecisionOptions: [
      { step: 0.01, pressBits: 0x00, releaseBits: 0x00 },
      { step: 0.005, pressBits: 0x01, releaseBits: 0x04 },
      { step: 0.001, pressBits: 0x02, releaseBits: 0x08 },
    ],
  },
}

/* -------------------------------------------------------------------------- */
/* keyFeatures：键盘高级改键                                                    */
/* -------------------------------------------------------------------------- */

/** 只开放「配置」改键 Tab，DKS/MT/TGL/RS/SOCD 全部不支持。 */
export const keyFeaturesConfigOnly: KeyFeaturesCapability = {
  type: 'keyFeatures',
  config: {
    config: true,
    dks: false,
    mt: false,
    tgl: false,
    rs: false,
    socd: false,
    advanced: false,
  },
}

/** 支持全套 DKS/MT/TGL/RS/SOCD，但不含高级改键页。 */
export const keyFeaturesFull: KeyFeaturesCapability = {
  type: 'keyFeatures',
  config: {
    config: true,
    dks: true,
    mt: true,
    tgl: true,
    rs: true,
    socd: true,
    advanced: false,
  },
}

/** 全部高级功能均不支持。 */
export const keyFeaturesDisabled: KeyFeaturesCapability = {
  type: 'keyFeatures',
  config: {
    dks: false,
    mt: false,
    tgl: false,
    rs: false,
    socd: false,
  },
}

/** 仅支持高级功能 */
export const keyFeaturesAdvancedOnly: KeyFeaturesCapability = {
  type: 'keyFeatures',
  config: {
    config: true,
    dks: false,
    mt: false,
    tgl: false,
    rs: false,
    socd: false,
    advanced: true,
  },
}

/* -------------------------------------------------------------------------- */
/* keyboardPerf：键盘性能                                                      */
/* -------------------------------------------------------------------------- */

/** 125/250/500/1000Hz 四档回报率（字节值为分频系数）。 */
export const reportRateOptions1000Hz: NonNullable<
  KeyboardPerfCapability['config']['reportRateOptions']
> = [
  { value: 0x08, label: '125Hz' },
  { value: 0x04, label: '250Hz' },
  { value: 0x02, label: '500Hz' },
  { value: 0x01, label: '1000Hz' },
]

export const keyboardPerfV700: KeyboardPerfCapability = {
  type: 'keyboardPerf',
  config: {
    reportRateOptions: reportRateOptions1000Hz,
    sleepTimeRange: { min: 2, max: 120 },
  },
}

export const keyboardPerfGcomeAp: KeyboardPerfCapability = {
  type: 'keyboardPerf',
  config: {
    enablePerformanceConfig: true,
    macMode: true,
  },
}

/* -------------------------------------------------------------------------- */
/* 底座                                                                       */
/* -------------------------------------------------------------------------- */

/** 底座无线信道 0-7。 */
export const baseChannel0To7: BaseChannelCapability = {
  type: 'baseChannel',
  config: {
    enable: true,
    min: 0,
    max: 7,
  },
}

export const baseChildDeviceEnabled: BaseChildDeviceCapability = {
  type: 'baseChildDevice',
  config: {
    enable: true,
  },
}

export const baseChildDeviceDisabled: BaseChildDeviceCapability = {
  type: 'baseChildDevice',
  config: {
    enable: false,
  },
}

/** 按型号页面布局创建 240x135 图片屏能力。 */
export function createBaseScreenImageCapability(
  config: Required<Pick<BaseScreenCapability['config'], 'maxScreenSlots' | 'systemPages'>>,
): BaseScreenCapability {
  return {
    type: 'baseScreen',
    config: {
      enable: true,
      width: 240,
      height: 135,
      screenType: 'screen',
      screenUsagePage: 65280,
      screenUsage: 1,
      ...config,
    },
  }
}

/** 240x135 Logo 屏灯。 */
export const baseScreenLogo: BaseScreenCapability = {
  type: 'baseScreen',
  config: {
    enable: true,
    width: 240,
    height: 135,
    screenType: 'logo',
    screenUsagePage: 65280,
    screenUsage: 1,
  },
}

/** 无屏底座：保留屏幕接口描述但不启用。 */
export const baseScreenDisabled: BaseScreenCapability = {
  type: 'baseScreen',
  config: {
    enable: false,
    width: 240,
    height: 135,
    screenUsagePage: 65280,
    screenUsage: 1,
  },
}

/* -------------------------------------------------------------------------- */
/* mousePerf：性能页开放的回报率 / 传感器模式 / 按键扫描率                        */
/* -------------------------------------------------------------------------- */

/** 零售档位：6 档传感器模式 + 125Hz~8000Hz 七档回报率。 */
const RETAIL_SENSOR_MODES: SensorModeOption[] = [
  { value: 5, preset: 'frenzy' },
  { value: 4, preset: 'competitive' },
  { value: 3, preset: 'hyperCore' },
  { value: 2, preset: 'firepower' },
  { value: 1, preset: 'balanced' },
  { value: 0, preset: 'office' },
]
const RETAIL_REPORT_RATES: ReportRate[] = [0x08, 0x04, 0x02, 0x01, 0x84, 0x82, 0x81]
const RETAIL_REPORT_RATE_MODES: MousePerfCapability['config']['reportRateModes'] = {
  0x08: [1, 0],
  0x04: [1, 0],
  0x02: [2, 1, 0],
  0x01: [5, 4, 3, 2, 1],
  0x84: [5, 4, 3, 2],
  0x82: [5, 4, 3],
  0x81: [5, 4, 3],
}

/** 网吧档位：只开放高性能三档，回报率从 1000Hz 起。 */
const CYBERCAFE_SENSOR_MODES: SensorModeOption[] = [
  { value: 5, preset: 'frenzy' },
  { value: 4, preset: 'competitive' },
  { value: 3, preset: 'hyperCore' },
]
const CYBERCAFE_REPORT_RATES: ReportRate[] = [0x01, 0x84, 0x82, 0x81]
const CYBERCAFE_REPORT_RATE_MODES: MousePerfCapability['config']['reportRateModes'] = {
  0x01: [5, 4, 3],
  0x84: [5, 4, 3],
  0x82: [5, 4, 3],
  0x81: [5, 4, 3],
}

/** PAW3311 档位：协议值 5 是「超核」而非「狂爆竞技」，回报率编号也另成一套。 */
const PAW3311_SENSOR_MODES: SensorModeOption[] = [
  { value: 5, preset: 'hyperCore' },
  { value: 2, preset: 'firepower' },
  { value: 0, preset: 'office' },
]
const PAW3311_REPORT_RATES: ReportRate[] = [0x00, 0x01, 0x02, 0x03]
const PAW3311_REPORT_RATE_MODES: MousePerfCapability['config']['reportRateModes'] = {
  0x00: [0, 2],
  0x01: [0, 2],
  0x02: [0, 2, 5],
  0x03: [0, 2, 5],
}

/** 光微动机型开放的按键扫描率档位。 */
const OPTICAL_KEY_SCAN_RATES: KeyScanRate[] = [3, 4, 5, 6]

const MOUSE_PERF_TIERS = {
  retail: {
    sensorModes: RETAIL_SENSOR_MODES,
    reportRates: RETAIL_REPORT_RATES,
    reportRateModes: RETAIL_REPORT_RATE_MODES,
  },
  cybercafe: {
    sensorModes: CYBERCAFE_SENSOR_MODES,
    reportRates: CYBERCAFE_REPORT_RATES,
    reportRateModes: CYBERCAFE_REPORT_RATE_MODES,
  },
  paw3311: {
    sensorModes: PAW3311_SENSOR_MODES,
    reportRates: PAW3311_REPORT_RATES,
    reportRateModes: PAW3311_REPORT_RATE_MODES,
  },
} as const

/**
 * 组装鼠标性能能力。
 *
 * @param tier         档位组：零售 6 档 / 网吧 3 档 / PAW3311 三档
 * @param sensorScanRateLabel 竞技档文案里的传感器扫描率标称值（PAW3311 无竞技档，可省略）
 * @param keyScanRate  是否开放按键扫描率卡片（光微动机型为 true）
 * @param rfPacketType 通讯协议切换：`'always'` 无条件开放、`'wirelessOnly'` 仅无线且接收器
 *                     版本高于 0.74、省略则不开放
 */
export function mousePerfCap(options: {
  tier: keyof typeof MOUSE_PERF_TIERS
  sensorScanRateLabel?: string
  keyScanRate?: boolean
  rfPacketType?: 'always' | 'wirelessOnly'
}): MousePerfCapability {
  const tier = MOUSE_PERF_TIERS[options.tier]
  return {
    type: 'mousePerf',
    config: {
      enable: true,
      reportRates: tier.reportRates,
      reportRateModes: tier.reportRateModes,
      sensorModes: tier.sensorModes,
      ...(options.sensorScanRateLabel
        ? { sensorScanRateLabel: options.sensorScanRateLabel }
        : null),
      ...(options.keyScanRate ? { keyScanRates: OPTICAL_KEY_SCAN_RATES } : null),
      ...(options.rfPacketType === 'always'
        ? { rfPacketType: {} }
        : options.rfPacketType === 'wirelessOnly'
          ? { rfPacketType: { wirelessOnly: true, minReceiverVersion: '0.74' } }
          : null),
    },
  }
}

/** 没有性能页的鼠标。 */
export const mousePerfDisabled: MousePerfCapability = {
  type: 'mousePerf',
  config: {
    enable: false,
    reportRates: [],
    reportRateModes: {},
    sensorModes: [],
  },
}

/* -------------------------------------------------------------------------- */
/* mouse-rt                                                                   */
/* -------------------------------------------------------------------------- */

/** VT2 RT 系列共用的按键行程 / 振动 / 音效范围，云端预设按 configType 共享。 */
export const mouseRtVT2: MouseRTCapability = {
  type: 'mouse-rt',
  config: {
    enable: true,
    configType: 'VT2:mouse-rt',
    triggerTravel: { min: 1, max: 10, default: 5 },
    rtTriggerLevel: { min: 1, max: 10, default: 2 },
    vibration: { min: 0, max: 10, default: 0 },
    vibrationFeedbackType: { min: 0, max: 2, default: 1 },
    sound: { min: 1, max: 10, default: 1 },
    volume: { min: 0, max: 100, default: 50 },
  },
}
